import { AvatarItem } from '@components/shared/avatar-item.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { ExpertProfileButton } from '@components/shared/expert-profile-button.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import {
  useMissionAssessmentCoreQueryOptions,
  useMissionAssessmentServicesQueryOptions,
} from '@queries/mission-assessments.query';
import { useQuery } from '@tanstack/react-query';
import { formatDate } from '@utils/date.util';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

type MaInfoTileProps = {
  isMilitaryTrl: boolean;
  missionAssessmentId: string;
  technologyId: string;
};

const PREVIEW_ITEM_LIMIT = 3;
const recommendationSectionClassName = 'flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3';
const recommendationSectionTitleClassName = 'text-xs font-medium text-gray-700';

export const MaInfoTile: FC<MaInfoTileProps> = (props) => {
  const { isMilitaryTrl, missionAssessmentId, technologyId } = props;

  const navigate = useNavigate();
  const coreQueryOptions = useMissionAssessmentCoreQueryOptions(missionAssessmentId);
  const { data: missionAssessment, isPending: isCorePending } = useQuery(coreQueryOptions);

  const servicesQueryOptions = useMissionAssessmentServicesQueryOptions(missionAssessmentId);
  const shouldFetchServices = Boolean(isMilitaryTrl && missionAssessment && servicesQueryOptions.enabled);
  const { data: servicesData, isPending: isServicesPending } = useQuery({
    ...servicesQueryOptions,
    enabled: shouldFetchServices,
  });

  const handleNavigateToMa = (): void => {
    navigate(`/technology/${technologyId}?tab=ma&maId=${missionAssessmentId}`);
  };

  const recommendedServices = servicesData?.recommendedServices ?? [];
  const sortedRecommendedServices = useMemo(
    () => [...recommendedServices].sort((a, b) => a.service.name.localeCompare(b.service.name)),
    [recommendedServices],
  );
  const previewRecommendedServices = sortedRecommendedServices.slice(0, PREVIEW_ITEM_LIMIT);
  const taskRecommendations = servicesData?.taskRecommendations ?? [];
  const previewTaskRecommendations = taskRecommendations.slice(0, PREVIEW_ITEM_LIMIT);

  const hasRecommendedServices = previewRecommendedServices.length > 0;
  const hasTaskRecommendations = previewTaskRecommendations.length > 0;
  const isLoading = isCorePending || (shouldFetchServices && isServicesPending);

  const expert = missionAssessment?.expert;
  const createdAt = missionAssessment?.createdAt;

  return (
    <Tile
      emptyState={{ title: 'No Mission Assessment' }}
      header={{
        iconButton: {
          icon: <ArrowRight />,
          onClick: handleNavigateToMa,
        },
        title: 'Mission Assessment',
      }}
      hoverReveal
      isEmpty={!missionAssessment && !isCorePending}
      isLoading={isLoading}
    >
      {missionAssessment && (
        <div className="flex flex-col gap-4">
          {/* Expert Profile */}
          {expert && (
            <div className="flex flex-col gap-1">
              <BlueLabel
                text="Assessed by"
                className="text-xs"
              />
              <ExpertProfileButton
                className="w-auto"
                expert={expert}
                size="sm"
              />
            </div>
          )}

          {/* Assessment Date */}
          {createdAt && (
            <TextWithLabel
              className="flex-col-reverse text-sm"
              label="Assessment Date"
              labelClassName="text-xs"
              title={formatDate(createdAt)}
            />
          )}

          {isMilitaryTrl && hasTaskRecommendations && (
            <div className={recommendationSectionClassName}>
              <div className={recommendationSectionTitleClassName}>
                Task Recommendations ({taskRecommendations.length})
              </div>
              <div className="space-y-2">
                {previewTaskRecommendations.map((taskRecommendation) => (
                  <div
                    className="wrap-break-word rounded-md border border-gray-200 bg-gray-100 p-2 text-sm text-gray-700"
                    key={taskRecommendation.id}
                  >
                    <ExpandableText
                      maxLines={2}
                      text={taskRecommendation.description}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {isMilitaryTrl && hasRecommendedServices && (
            <div className={recommendationSectionClassName}>
              <div className={recommendationSectionTitleClassName}>
                Recommended Services ({recommendedServices.length})
              </div>
              <div className="space-y-2">
                {previewRecommendedServices.map(({ service, unit }) => (
                  <AvatarItem
                    altText={`${service.name} Icon`}
                    className="min-w-0 items-center"
                    imageUrl={`/images/services/${service.slug}.png`}
                    initialText={service.name}
                    key={`${service.id}-${unit}`}
                    size="sm"
                    subtitle={unit}
                    title={service.name}
                    variant="sm"
                  />
                ))}
              </div>
            </div>
          )}

          <Button
            className="w-full gap-2"
            onClick={handleNavigateToMa}
            type="button"
            variant="secondary"
          >
            <span>Open Mission Assessment</span>
            <ExternalLink className="size-4" />
          </Button>
        </div>
      )}
    </Tile>
  );
};
